import * as React from "react";
import { useConvex, useMutation } from "convex/react";
import { AlertTriangle, FileJson, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/convexApi";

/**
 * Master bootstrap: load the vendor and GL-account catalog from a QuickBooks /
 * Stampli export. Always dry-run first — the plan below is computed server-side
 * against the live catalog, and nothing is written until Apply.
 */

interface ImportGlRow {
  qboAccountName: string;
  label?: string;
  active?: boolean;
}

interface ImportVendorRow {
  displayName: string;
  qboPayeeName: string;
  billingAddress?: string;
  paymentMethod?: string;
  invoiceDiscountTerms?: string;
  defaultPaymentTerms?: string;
  aliases?: string[];
  emailDomains?: string[];
  active?: boolean;
  defaultGlAccountName?: string;
}

interface ImportPayload {
  glAccounts: ImportGlRow[];
  vendors: ImportVendorRow[];
}

interface PlanEntry {
  name: string;
  changes: string[];
}

interface PlanSide {
  create: string[];
  update: PlanEntry[];
  unchanged: number;
  issues: { name: string; reason: string }[];
}

interface ImportPlan {
  glAccounts: PlanSide;
  vendors: PlanSide;
  catalogCounts: { vendors: number; glAccounts: number };
}

const CHUNK = 100;

function asStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out = value.filter((v): v is string => typeof v === "string" && !!v.trim());
  return out.length ? out : undefined;
}

function str(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

/**
 * Accept either the flat `{glAccounts, vendors}` shape or the richer bundle the
 * Stampli pull produces, where GL accounts arrive as bare strings and vendors
 * carry extra provenance fields we do not store.
 */
function parsePayload(raw: unknown): ImportPayload {
  if (!raw || typeof raw !== "object") throw new Error("File is not a JSON object");
  const source = raw as Record<string, unknown>;

  const glSource = Array.isArray(source.glAccounts)
    ? source.glAccounts
    : Array.isArray((source.glAccounts as Record<string, unknown> | undefined)?.codeable)
      ? ((source.glAccounts as Record<string, unknown>).codeable as unknown[])
      : [];
  const glAccounts: ImportGlRow[] = glSource
    .map((entry): ImportGlRow | null => {
      if (typeof entry === "string") {
        const name = entry.trim();
        return name ? { qboAccountName: name } : null;
      }
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const name = str(row.qboAccountName) ?? str(row.name);
      if (!name) return null;
      return {
        qboAccountName: name,
        label: str(row.label),
        active: typeof row.active === "boolean" ? row.active : undefined,
      };
    })
    .filter((row): row is ImportGlRow => row !== null);

  const vendorSource = Array.isArray(source.vendors) ? source.vendors : [];
  const vendors: ImportVendorRow[] = vendorSource
    .map((entry): ImportVendorRow | null => {
      if (!entry || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const displayName = str(row.displayName) ?? str(row.name);
      const qboPayeeName = str(row.qboPayeeName) ?? displayName;
      if (!displayName || !qboPayeeName) return null;
      return {
        displayName,
        qboPayeeName,
        billingAddress: str(row.billingAddress),
        paymentMethod: str(row.paymentMethod),
        invoiceDiscountTerms: str(row.invoiceDiscountTerms),
        defaultPaymentTerms: str(row.defaultPaymentTerms),
        aliases: asStringArray(row.aliases),
        emailDomains: asStringArray(row.emailDomains),
        active: typeof row.active === "boolean" ? row.active : undefined,
        defaultGlAccountName:
          str(row.defaultGlAccountName) ?? str(row.suggestedGlAccount),
      };
    })
    .filter((row): row is ImportVendorRow => row !== null);

  if (!glAccounts.length && !vendors.length) {
    throw new Error("No vendors or GL accounts found in this file");
  }
  return { glAccounts, vendors };
}

function PlanSection({ title, side }: { title: string; side: PlanSide }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-sm font-medium">{title}</p>
      <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs tabular-nums">
        <span>
          <span className="text-foreground font-medium">{side.create.length}</span>{" "}
          to create
        </span>
        <span>
          <span className="text-foreground font-medium">{side.update.length}</span>{" "}
          to update
        </span>
        <span>{side.unchanged} unchanged</span>
        {side.issues.length > 0 && (
          <span className="text-destructive">{side.issues.length} need attention</span>
        )}
      </div>
      {side.create.length > 0 && (
        <p className="text-muted-foreground mt-2 line-clamp-2 text-xs">
          New: {side.create.slice(0, 8).join(", ")}
          {side.create.length > 8 ? ` +${side.create.length - 8} more` : ""}
        </p>
      )}
      {side.issues.length > 0 && (
        <ul className="mt-2 space-y-1">
          {side.issues.slice(0, 8).map((issue) => (
            <li key={`${issue.name}-${issue.reason}`} className="text-xs">
              <AlertTriangle className="text-destructive mr-1 inline h-3 w-3" />
              <span className="font-medium">{issue.name || "(unnamed)"}</span>{" "}
              <span className="text-muted-foreground">{issue.reason}</span>
            </li>
          ))}
          {side.issues.length > 8 && (
            <li className="text-muted-foreground text-xs">
              +{side.issues.length - 8} more
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export function ImportTab({
  authUserId,
  canManage,
}: {
  authUserId: string | undefined;
  canManage: boolean;
}) {
  const convex = useConvex();
  const applyChunk = useMutation(api.accountingBillBootstrap.applyMasterImportChunk);

  const [fileName, setFileName] = React.useState<string | null>(null);
  const [payload, setPayload] = React.useState<ImportPayload | null>(null);
  const [plan, setPlan] = React.useState<ImportPlan | null>(null);
  const [overwrite, setOverwrite] = React.useState(false);
  const [isPlanning, setIsPlanning] = React.useState(false);
  const [progress, setProgress] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const runPlan = React.useCallback(
    async (next: ImportPayload, nextOverwrite: boolean) => {
      if (!authUserId) return;
      setIsPlanning(true);
      setPlan(null);
      try {
        const result = (await convex.query(
          api.accountingBillBootstrap.planMasterImport,
          {
            authUserId,
            glAccounts: next.glAccounts,
            vendors: next.vendors,
            overwrite: nextOverwrite,
          },
        )) as ImportPlan;
        setPlan(result);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not build the plan");
      } finally {
        setIsPlanning(false);
      }
    },
    [authUserId, convex],
  );

  const handleFile = async (file: File) => {
    try {
      const parsed = parsePayload(JSON.parse(await file.text()));
      setFileName(file.name);
      setPayload(parsed);
      await runPlan(parsed, overwrite);
    } catch (err) {
      setPayload(null);
      setPlan(null);
      setFileName(null);
      toast.error(err instanceof Error ? err.message : "Could not read this file");
    }
  };

  const handleApply = async () => {
    if (!authUserId || !payload) return;
    setProgress("Starting…");
    try {
      let glCreated = 0;
      let glUpdated = 0;
      let vendorCreated = 0;
      let vendorUpdated = 0;
      const skipped: { name: string; reason: string }[] = [];

      // GL accounts first: vendor defaults resolve against them by name.
      for (let i = 0; i < payload.glAccounts.length; i += CHUNK) {
        setProgress(
          `GL accounts ${Math.min(i + CHUNK, payload.glAccounts.length)} / ${payload.glAccounts.length}`,
        );
        const result = await applyChunk({
          authUserId,
          glAccounts: payload.glAccounts.slice(i, i + CHUNK),
          overwrite,
        });
        glCreated += result.glCreated;
        glUpdated += result.glUpdated;
        skipped.push(...result.skipped);
      }
      for (let i = 0; i < payload.vendors.length; i += CHUNK) {
        setProgress(
          `Vendors ${Math.min(i + CHUNK, payload.vendors.length)} / ${payload.vendors.length}`,
        );
        const result = await applyChunk({
          authUserId,
          vendors: payload.vendors.slice(i, i + CHUNK),
          overwrite,
        });
        vendorCreated += result.vendorCreated;
        vendorUpdated += result.vendorUpdated;
        skipped.push(...result.skipped);
      }

      toast.success(
        `Imported — ${glCreated} GL accounts and ${vendorCreated} vendors created, ${glUpdated + vendorUpdated} updated${
          skipped.length ? `, ${skipped.length} noted` : ""
        }`,
      );
      await runPlan(payload, overwrite);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Import failed partway");
    } finally {
      setProgress(null);
    }
  };

  const nothingToDo =
    plan !== null &&
    !plan.glAccounts.create.length &&
    !plan.glAccounts.update.length &&
    !plan.vendors.create.length &&
    !plan.vendors.update.length;

  return (
    <div className="space-y-3">
      <p className="text-muted-foreground text-sm">
        Bootstrap the catalog from a QuickBooks or Stampli export. The plan is a
        dry run — nothing is written until you apply. Re-importing the same file
        is a no-op, and an import never deletes or deactivates anything.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
            e.target.value = "";
          }}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!canManage}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="mr-2 h-4 w-4" />
          Choose JSON file
        </Button>
        {fileName && (
          <span className="text-muted-foreground flex items-center gap-1.5 text-sm">
            <FileJson className="h-3.5 w-3.5" />
            {fileName}
          </span>
        )}
        {payload && (
          <span className="text-muted-foreground text-xs tabular-nums">
            {payload.glAccounts.length} GL accounts · {payload.vendors.length} vendors
          </span>
        )}
      </div>

      {payload && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="import-overwrite"
            checked={overwrite}
            onCheckedChange={(checked) => {
              const next = checked === true;
              setOverwrite(next);
              void runPlan(payload, next);
            }}
          />
          <Label htmlFor="import-overwrite" className="text-sm font-normal">
            Overwrite fields that already have a value
          </Label>
          <span className="text-muted-foreground text-xs">
            (off: only fills blanks, so hand edits survive)
          </span>
        </div>
      )}

      {isPlanning && <Skeleton className="h-28 w-full" />}

      {plan && !isPlanning && (
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs">
            Current catalog: {plan.catalogCounts.glAccounts} GL accounts,{" "}
            {plan.catalogCounts.vendors} vendors.
          </p>
          <PlanSection title="GL accounts" side={plan.glAccounts} />
          <PlanSection title="Vendors" side={plan.vendors} />
          <div className="flex items-center justify-end gap-2">
            {progress && (
              <span className="text-muted-foreground text-xs tabular-nums">
                {progress}
              </span>
            )}
            <Button
              size="sm"
              disabled={!canManage || !!progress || nothingToDo}
              onClick={handleApply}
            >
              {progress
                ? "Importing…"
                : nothingToDo
                  ? "Nothing to import"
                  : "Apply import"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
